import React, { FC, useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import { GET_TOP_DOWN_RATES } from "~/graphql/query";
import { UPDATE_WORK, UPDATE_MANY_WORK } from "~/graphql/mutation";
import {
  TopDownPricing,
  TeamMembership,
  Work,
  PricingTarget,
  FeeEarnerLevel,
} from "~/design-system";
import { useMutation } from "../../hooks";
import { GpmThreshold } from "~/design-system";
import { TeamMembershipPartsFragment } from "~/gql/graphql";

import styles from "./top-down-pricing-container.css";

const { ["container"]: containerClassName, ["body-section"]: bodySectionClassName } = styles as any;

type TopDownPricingContainerProps = {
  matterId: number;
  currency: { symbol: string; code: string };
  gpmThreshold: GpmThreshold;
  clientId?: number;
  organisationalUnitId?: number;
  topDownRates: TeamMembershipPartsFragment[];
  topDownWork: Work[];
  pricingTarget: PricingTarget;
  topDownFeeTarget: number;
  onTopDownFeeTargetChange: (feeTarget: number | null) => void;
  topDownHoursTarget: number;
  onTopDownHoursTargetChange: (hoursTarget: number | null) => void;
  topDownRateDiscount: number;
  onTopDownRateDiscountChange: (rateDiscount: number | null) => void;
  onFeeEarnerLevelRateChange: (oldRateId: number, newRateId: number) => Promise<void>;
  onRemoveFeeEarnerLevel: (teamMembershipId: number, workIds: number[]) => Promise<void>;
  onAddFeeEarnerLevel: (
    feeEarnerLevelId: number,
    rateId: number
  ) => Promise<{ teamMembershipId?: number; work?: Work }>;
  feeEarnerLevels: FeeEarnerLevel[];
  refreshTrigger: number;
};

export const TopDownPricingContainer: FC<TopDownPricingContainerProps> = ({
  matterId,
  currency,
  gpmThreshold,
  clientId,
  organisationalUnitId,
  topDownRates,
  topDownWork,
  pricingTarget,
  topDownFeeTarget,
  onTopDownFeeTargetChange,
  topDownHoursTarget,
  onTopDownHoursTargetChange,
  topDownRateDiscount,
  onTopDownRateDiscountChange,
  onFeeEarnerLevelRateChange,
  onRemoveFeeEarnerLevel,
  onAddFeeEarnerLevel,
  feeEarnerLevels,
  refreshTrigger,
}) => {
  const [updateWork] = useMutation(UPDATE_WORK);
  const [updateManyWork] = useMutation(UPDATE_MANY_WORK);
  const [topDownRatesSorted, setTopDownRatesSorted] = useState<TeamMembershipPartsFragment[]>([]);

  const { data: ratesData = undefined, loading: loadingRates } = useQuery(GET_TOP_DOWN_RATES, {
    variables: {
      filter: {
        clientId,
        organisationalUnitId,
        currencyCode: currency.code,
        matterId,
      },
    },
  });

  useEffect(() => {
    setTopDownRatesSorted(
      [...topDownRates].sort(
        (a, b) => (a.rate?.feeEarnerLevel?.order ?? 0) - (b.rate?.feeEarnerLevel?.order ?? 0)
      )
    );
  }, [topDownRates]);
  const topDownRateOptions = ratesData?.getTopDownRates || [];

  return (
    <div className={containerClassName}>
      <div className={bodySectionClassName}>
        <TopDownPricing
          currencySymbol={currency.symbol}
          gpmThreshold={gpmThreshold}
          feeEarnerLevels={feeEarnerLevels}
          topDownRates={topDownRatesSorted as TeamMembership[]}
          topDownWork={topDownWork}
          topDownRateOptions={topDownRateOptions}
          pricingTarget={pricingTarget}
          topDownFeeTarget={topDownFeeTarget}
          onTopDownFeeTargetChange={onTopDownFeeTargetChange}
          topDownHoursTarget={topDownHoursTarget}
          onTopDownHoursTargetChange={onTopDownHoursTargetChange}
          topDownRateDiscount={topDownRateDiscount}
          onTopDownRateDiscountChange={onTopDownRateDiscountChange}
          onFeeEarnerLevelRateChange={async (oldRateId, newRateId, workId) => {
            await onFeeEarnerLevelRateChange(oldRateId, newRateId);
            if (workId !== null) {
              await updateWork({
                variables: {
                  workId,
                  data: {
                    selectedRate: { connect: { id: newRateId } },
                  },
                },
              });
            }
          }}
          onUpdateWork={(data) => {
            updateWork({
              variables: {
                workId: data.workId,
                data: {
                  ...(data.timeSingle != null && { timeSingle: { set: data.timeSingle } }),
                },
              },
            });
          }}
          onUpdateManyWorkHours={(data) => {
            updateManyWork({
              variables: {
                data: data.map(({ workId, hours, workEffort, rateDiscount }) => ({
                  id: workId,
                  payload: {
                    timeSingle: { set: hours },
                    workEffort: { set: workEffort },
                    topDownRateDiscount: { set: rateDiscount },
                  },
                })),
              },
            });
          }}
          onRemoveFeeEarnerLevel={onRemoveFeeEarnerLevel}
          onAddFeeEarnerLevel={async (feeEarnerLevelId) => {
            const newRate = topDownRateOptions.find(
              (rateGroup) => rateGroup.feeEarnerLevelId === feeEarnerLevelId
            )?.rates[0];
            if (newRate) {
              return onAddFeeEarnerLevel(feeEarnerLevelId, newRate.id);
            }
            return { teamMembershipId: undefined, work: undefined };
          }}
          loadingRates={loadingRates}
          refreshTrigger={refreshTrigger}
        />
      </div>
    </div>
  );
};
